/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useStyles } from './style';
import {
    ApplicationState,
    Api,
    KeyType,
    CredentialsReducer,
} from "../../../data/interfaces";
import ApiInfoBar from '../../shared/ApiInfoBar';
import OAuthCredentials from './OAuthCredentials';
import APIKeyCredentials from './APIKeyCredentials';
import { Box, Tabs, Tab, Tooltip, Typography } from '@material-ui/core';
import { TabPanel } from '../../shared/Tab/TabPanel';
import Settings from '../../../conf/settings';
import { checkCredentials } from '../../../data/operations';
import { PreLoaderLogo } from '../../../elements/PreLoaderLogo';

interface ApiCredentialsProps {
    apiInfo: Api,
    credentialInfo: CredentialsReducer,
    keyType: KeyType,
    dispatchCheckCredentials: () => void,
}

function ApiCredentials(props: ApiCredentialsProps) {

    const { apiInfo, keyType, credentialInfo, dispatchCheckCredentials } = props;
    const classes = useStyles();
    const { isKeysFetched, isKeyFetching, keys } = credentialInfo;

    const [securitySchemes, setSecuritySchemes] = useState<string[]>([]);
    const [selectedTab, setSelectedTab] = useState(0);
    const [showOAuthCredentials, setShowOAuthCredentials] = useState(false);

    useEffect(() => {
        if (apiInfo?.id) {
            dispatchCheckCredentials();
        }
        const securitySchemes = [];
        if (apiInfo?.securityScheme?.includes('oauth2')) {
            securitySchemes.push('oauth2');
        }
        if (Settings.apiKeyFeatureEnabled && apiInfo?.securityScheme?.includes('api_key')) {
            securitySchemes.push('api_key');
        }
        setSecuritySchemes(securitySchemes);
    }, [apiInfo]);

    useEffect(() => {
        if (Settings.apiTryoutTestKeyEnabled && isKeysFetched) {
            if (keys && keys.list?.length > 0) {
                setShowOAuthCredentials(true);
            } else {
                setShowOAuthCredentials(false);
            }
        }
    }, [isKeysFetched, keys]);

    return (
        <main className={classes.content}>
            <ApiInfoBar />
            { securitySchemes.length > 1 && !isKeysFetched && isKeyFetching &&
                <Box className={classes.loaderContainer}>
                    <PreLoaderLogo size="medium" />
                </Box>
            }
            { securitySchemes.length > 1 && (!Settings.apiTryoutTestKeyEnabled || showOAuthCredentials) && !isKeyFetching &&
                <Box px={6.5}>
                    <Box className={classes.tabsBox}>
                      <Tabs
                        value={selectedTab}
                        onChange={(_event, value) => setSelectedTab(value)}
                        TabIndicatorProps={{ className: classes.tabs }}
                      >
                        <Tab 
                            label={
                                <Tooltip title="API Key">
                                    <Typography className={classes.tabTextParent}>
                                    API Key
                                    </Typography>
                                </Tooltip>
                            } />
                        <Tab 
                            label={
                                <Tooltip title="OAuth">
                                    <Typography className={classes.tabTextParent}>
                                    OAuth
                                    </Typography>
                                </Tooltip>
                            } />
                      </Tabs>
                    </Box>
                    <TabPanel
                        selectedIndex={selectedTab}
                        index={0}
                        key={"API Key"}
                    >
                        <APIKeyCredentials keyType={keyType}/>
                    </TabPanel>
                    <TabPanel
                        selectedIndex={selectedTab}
                        index={1}
                        key={"OAuth"}
                    >
                        <Box
                            textAlign={"justify"}
                            pl={2}
                        >
                            <OAuthCredentials />
                        </Box>
                    </TabPanel>
                </Box>
            } 
            { securitySchemes.length === 1 && securitySchemes?.includes('oauth2') && (!Settings.apiTryoutTestKeyEnabled || showOAuthCredentials) &&
                <OAuthCredentials />
            }
            { securitySchemes?.includes('api_key') && !showOAuthCredentials && 
                <APIKeyCredentials keyType={keyType} />
            }
        </main>
    )
}

const mapDispatchToProps = {
    dispatchCheckCredentials: checkCredentials,
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        apiInfo: state.apiReducer.api,
        credentialInfo: state.credentialsReducer,
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ApiCredentials);
